import { useCallback } from 'react';
import { UseTransactionsTabNavigationProps } from './props';
import { useCurrentModal } from '../modals/useCurrentModal';
import useDepositSearchParams from '../deposit/useDepositSearchParams';
import { useWithdrawSearchParams } from '../withdraw';
import { useWalletsMe } from '../../api';
import { CoinType } from '../../models';
import { CoinID, CommonModalsKey, CookiesKey } from '../../enums';
import { useSearchParams } from '../useSearchParams';
import { useNavigate } from '../useNavigate';
import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { getCookie } from 'cookies-next';
import { OriginId } from '@starsoft/common/models/origin/id';

/**
 * Custom hook to manage the navigation and state of transaction tabs.
 * This hook provides a function to set the active tab based on the coin type.
 *
 * @param {UseTransactionsTabNavigationProps} props - The properties for the hook.
 * @param {Function} props.setValue - Function to set the value of a form field.
 *
 * @returns {Object} An object containing the setTab function.
 *
 * @example
 * // Usage example:
 * const { setTab } = useTransactionsTabNavigation({ setValue: (field, value, options) => {} });
 * setTab(CoinType.Crypto); // Sets the tab for the specified coin type
 */
export default function useTransactionsTabNavigation({
  setValue,
}: UseTransactionsTabNavigationProps) {
  // Hook to manage URL search parameters
  const search: URLSearchParams = useSearchParams();

  const originId = getCookie(CookiesKey.OriginId);
  // Hook to navigate between pages or components
  const navigate: NavigateFunction = useNavigate();
  // Hook to get the current modal key
  const modal = useCurrentModal(CommonModalsKey);

  // Retrieve the transaction step for withdrawal
  const { transactionStep: withdrawTransactionStep } =
    useWithdrawSearchParams();
  // Retrieve the transaction step for deposit
  const { transactionStep: depositTransactionStep } = useDepositSearchParams();
  // Determine the current step based on withdrawal or deposit
  const step = withdrawTransactionStep || depositTransactionStep;
  // Retrieve the current wallet information, enabled if the step is 'form'
  const { wallet } = useWalletsMe({
    enabled: step == 'form',
  });

  /**
   * Sets the active tab based on the specified coin type.
   * Updates the search parameters and navigates to the appropriate tab.
   *
   * @param {CoinType} coinType - The type of the coin to set the tab for.
   */
  const setTab = useCallback(
    (coinType: CoinType) => {
      // Retrieve accounts and current account from the wallet
      const accounts = wallet?.accounts;
      const currentAccount = wallet?.currentAccount;
      let coinId: bigint;

      // Determine the coin ID based on the current account or find it in the accounts list
      if (currentAccount?.coin?.type == coinType) {
        coinId = currentAccount?.coin?.id;
      } else {
        coinId = accounts?.find(account =>
          coinType == CoinType.Crypto &&
          originId?.toString() != OriginId.Brazil?.toString()
            ? account?.coin?.id == CoinID.USDT
            : account?.coin?.type == coinType,
        )?.coin?.id;
      }

      // Update search parameters for the coin type and coin ID
      search.delete('method-id');
      search.set('coin-type', coinType);
      search.set(
        'coin-id',
        String(
          coinId ??
            (coinType === CoinType.Crypto &&
            originId?.toString() != OriginId.Brazil?.toString()
              ? CoinID.USD
              : CoinID.BRL),
        ),
      );
      // Set the value of the 'coinId' field without marking it as dirty
      //@ts-ignore
      setValue('coinId', coinId, { shouldDirty: false });

      // Navigate to the updated search parameters and current modal
      navigate(
        {
          search: search.toString(),
          hash: modal,
        },
        {
          preventScrollReset: true,
        },
      );
    },
    [modal, navigate, search, wallet, setValue, originId],
  );

  // Return the setTab function to be used externally
  return {
    setTab,
  };
}

export type { UseTransactionsTabNavigationProps };
